import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { LPTokenIcon } from '@ui/components/LPTokenIcon';
import {
  RenderInDesktop,
  RenderInMobile,
} from '@ui/components/MobileOrDesktop';
import useFarmAprs from '@ui/hooks/useFarmAprs';
import useTotalGardenDeposit from '@ui/hooks/useTotalGardenDeposit';
import { formatTokenAmount } from '@ui/utils/format';
import { gardenTokenDict, getGardenName } from '@ui/utils/garden';
import { ColorThemeVars, InColorTheme, InGauze } from '@veno-app/chakra';
import React, { useEffect, useRef } from 'react';

import { GardenBooster } from './GardenBooster';
import GardenModalActions from './GardenModalActions';
import GardenViewGrow from './GardenViewGrow';
import GardenViewHarvest from './GardenViewHarvest';
import GardenViewPlant from './GardenViewPlant';
import { useGardenModalStore } from './useGardenModalStore';

const FOOTER_HEIGHT = '81px';

function GardenModal() {
  const { type, isOpen, close, activeView, colorTheme, coinMode } =
    useGardenModalStore();
  const modalBodyRef = useRef<HTMLDivElement>(null);

  const { isBoosted } = useFarmAprs();

  const { totalBalance } = useTotalGardenDeposit(type);

  // scroll top automatically when activeView changed
  useEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scroll(0, 0);
    }
  }, [activeView]);

  return (
    <>
      <Modal
        variant="wholeScreen"
        scrollBehavior="inside"
        size={{
          base: 'full',
          desktop: '3xl',
        }}
        isOpen={isOpen}
        onClose={close}
      >
        <ModalOverlay bg="bg-gauze" />
        <InColorTheme colorTheme={colorTheme} isRoot>
          <ModalContent
            position="relative"
            bg={`var(${ColorThemeVars.mainColor})`}
          >
            <ModalHeader
              display={{
                base: 'flex',
                desktop: 'none',
              }}
              flexDir="column"
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Flex flex={1}>{isBoosted && <GardenBooster />}</Flex>
                <Text
                  as="h1"
                  flex={1}
                  textAlign="center"
                  textStyle={{
                    base: 'body',
                    desktop: 'h4',
                  }}
                >
                  {getGardenName(coinMode, type, false)}
                </Text>
                <Box flex={1} />
              </Flex>
              <Box
                mx="auto"
                textAlign="center"
                textStyle="caption"
                color="text-light"
              >
                <LPTokenIcon coinMode={coinMode} gardenType={type}>
                  TVL{' '}
                  {formatTokenAmount(
                    totalBalance,
                    3,
                    gardenTokenDict[coinMode][type].decimals,
                  )}
                </LPTokenIcon>
              </Box>
            </ModalHeader>
            <RenderInMobile>
              <ModalCloseButton variant="smallCard" size="xs" />
            </RenderInMobile>
            <RenderInDesktop>
              <InGauze>
                <InColorTheme colorTheme={colorTheme}>
                  <Flex
                    position="absolute"
                    left="calc(100% + 10px)"
                    top="0"
                    flexDir="column"
                    alignItems="center"
                  >
                    <ModalCloseButton
                      variant="smallCard"
                      size="xs"
                      position="static"
                    />
                    <GardenModalActions
                      activeView={activeView}
                      flexDir="column"
                      mt="31px"
                      gap="24px"
                    />
                  </Flex>
                </InColorTheme>
              </InGauze>
            </RenderInDesktop>
            <ModalBody
              ref={modalBodyRef}
              pb={{
                base: FOOTER_HEIGHT,
                desktop: 0,
              }}
              px={0}
              position="relative"
            >
              {activeView === 'PLANT' && <GardenViewPlant gardenType={type} />}
              {activeView === 'WITHDRAW' && (
                <GardenViewPlant gardenType={type} isWithdraw={true} />
              )}
              {activeView === 'GROW_WATER' && (
                <GardenViewGrow gardenType={type} />
              )}
              {activeView === 'GROW_HELPERS' && (
                <GardenViewGrow gardenType={type} />
              )}
              {activeView === 'HARVEST' && (
                <GardenViewHarvest gardenType={type} />
              )}
            </ModalBody>
            <ModalFooter
              display={{
                desktop: 'none',
              }}
              pb={3}
              pt={3}
              position="absolute"
              bottom={0}
              w="full"
              _before={{
                content: '" "',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                backdropFilter: 'auto',
                backdropBlur: '2px',
                backgroundColor: `var(${ColorThemeVars.mainColor})`,
                filter: 'opacity(0.85)',
              }}
            >
              <GardenModalActions activeView={activeView} position="relative" />
            </ModalFooter>
          </ModalContent>
        </InColorTheme>
      </Modal>
    </>
  );
}

export default GardenModal;
